// web/src/screens/admin/tasks_edit.ts — v3.2 (migration 0038).
//
// Task editor. v3.2 changes:
//   - Correct answer is a plain text input (was a Text/short_code picker).
//   - Alt-accepted answer(s) is a plain TEXT[] (was an alt_accepted_textset).
//   - "Prompt(s)" → "Task instruction(s)" (rename only; still a TextSet).
//   - "Text-effect" section renamed "Text to be shown (with optional effect)".
//   - The try_again retry-prompt tip is removed from speak widgets.

import { renderAdminShell, showAdminToast } from "./admin_shell.js";
import {
  adminGet, adminPost, adminPatch, adminDelete, adminUploadImage, AdminError,
} from "../../api/admin.js";
import { mountTextEffectEditor } from "./text_effect_editor.js";
import {
  mountTextSetLVInput,
  type TextSetLVInputHandle,
} from "./ref_inputs.js";
import {
  mountSwitchableTextSetWidget,
  type SwitchableTextSetHandle,
  type SwitchableTextSetOption,
} from "./switchable_textset.js";
import { saveWithSharedEditConfirm } from "./shared_edit_confirm.js";
import {
  USER_PROMPT_VARS, promptVarsInfoBox,
  mountOverridePromptField, makePromptTextarea, type OverridePromptHandle,
} from "./prompt_vars.js";
import type {
  AdminGameDTO, AdminCardDTO, AdminTaskDTO, AdminTaskActionDTO,
  AdminTextSetDTO, AdminTextEffectDTO, AdminTextEffectType,
  AdminRouteProps, AdminActionType,
} from "../../types/api.js";

function navigateAdmin(path: string): void {
  history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function field(parent: HTMLElement, label: string, input: HTMLElement, tip?: string): void {
  const wrap = document.createElement("label");
  wrap.setAttribute("style", "display: flex; flex-direction: column; gap: 4px; font-size: 13px; color: #374151;");
  const lab = document.createElement("span");
  lab.textContent = label;
  lab.setAttribute("style", "font-weight: 500;");
  wrap.appendChild(lab);
  wrap.appendChild(input);
  if (tip) {
    const tipEl = document.createElement("span");
    tipEl.textContent = tip;
    tipEl.setAttribute("style", "font-size: 11px; color: #6b7280; font-style: italic;");
    wrap.appendChild(tipEl);
  }
  parent.appendChild(wrap);
}

function makeInput(type: string): HTMLInputElement {
  const el = document.createElement("input");
  el.type = type;
  el.setAttribute("style", "padding: 6px 10px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 14px;");
  return el;
}

function makeSelect(): HTMLSelectElement {
  const el = document.createElement("select");
  el.setAttribute("style", "padding: 6px 10px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 14px; background: white;");
  return el;
}

// ── Alt-accepted answers (plain []string editor) ─────────────────────────

interface StringListHandle {
  value(): string[];
  setValue(v: string[]): void;
  unmount(): void;
}

function mountStringListInput(host: HTMLElement, opts: { initial?: string[]; placeholder?: string }): StringListHandle {
  let items = (opts.initial ?? []).slice();
  const root = document.createElement("div");
  root.setAttribute("style", "display: flex; flex-direction: column; gap: 6px; max-width: 520px;");
  const list = document.createElement("div");
  list.setAttribute("style", "display: flex; flex-direction: column; gap: 4px;");
  root.appendChild(list);
  const footer = document.createElement("div");
  footer.setAttribute("style", "display: flex; gap: 6px;");
  root.appendChild(footer);
  const addBtn = document.createElement("button");
  addBtn.type = "button";
  addBtn.textContent = "+ Add answer";
  addBtn.setAttribute("style", "padding: 4px 10px; background: #e5e7eb; color: #111827; border: 0; border-radius: 4px; cursor: pointer; font-size: 12px;");
  footer.appendChild(addBtn);

  function render(): void {
    list.replaceChildren();
    if (items.length === 0) {
      const hint = document.createElement("p");
      hint.textContent = "(no alternative answers)";
      hint.setAttribute("style", "margin: 0; font-size: 12px; color: #9ca3af; font-style: italic;");
      list.appendChild(hint);
    }
    for (let i = 0; i < items.length; i++) {
      const row = document.createElement("div");
      row.setAttribute("style", "display: flex; gap: 6px; align-items: center;");
      const inp = document.createElement("input");
      inp.type = "text";
      inp.value = items[i] ?? "";
      inp.placeholder = opts.placeholder ?? "Alternative accepted answer";
      inp.setAttribute("style", "padding: 6px 10px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 14px; flex: 1;");
      inp.addEventListener("input", () => { items[i] = inp.value; });
      row.appendChild(inp);
      const rm = document.createElement("button");
      rm.type = "button";
      rm.textContent = "✕";
      rm.setAttribute("style", "padding: 4px 8px; background: #e5e7eb; color: #111827; border: 0; border-radius: 4px; cursor: pointer; font-size: 12px;");
      rm.addEventListener("click", () => {
        items.splice(i, 1);
        render();
      });
      row.appendChild(rm);
      list.appendChild(row);
    }
  }

  addBtn.addEventListener("click", () => {
    items.push("");
    render();
    const inputs = list.querySelectorAll<HTMLInputElement>("input[type=text]");
    inputs[inputs.length - 1]?.focus();
  });

  render();
  host.replaceChildren(root);

  return {
    value: () => items.filter((s) => s.trim() !== "").map((s) => s.trim()),
    setValue(v: string[]): void { items = v.slice(); render(); },
    unmount(): void { host.replaceChildren(); },
  };
}

// ── Per-slot widget (unchanged from v3.1 structure) ──────────────────────

interface SlotPickerOption { id: string; label: string; }

const ACTION_TYPE_LABELS: Record<AdminActionType, string> = {
  next_card: "Next card",
  try_again: "Try again",
  next_task: "Next task",
};

interface SlotWidget {
  root: HTMLElement;
  value(): AdminTaskActionDTO | null;
  setInitial(a: AdminTaskActionDTO | null): void;
  refreshOptions(textsets: SlotPickerOption[], tasks: SlotPickerOption[]): void;
}

type SpeakMode = "initial" | "shared" | "non_shared";

interface SpeakTextSetWidget {
  root: HTMLElement;
  value(): {
    speak_textset_id?: string | null;
    speak_textset_short_code?: string | null;
    speak_textset_members?: string[];
  };
  setInitial(opts: {
    speakTextSetID?: string | null;
    speakTextSetShortCode?: string | null;
    speakTextSetLVMembers?: string[];
  }): void;
  refreshOptions(textsets: SlotPickerOption[]): void;
  setVisible(v: boolean): void;
}

function mountSpeakTextSetWidget(parent: HTMLElement): SpeakTextSetWidget {
  const wrap = document.createElement("div");
  wrap.setAttribute("style", "display: flex; flex-direction: column; gap: 4px;");
  const labelRow = document.createElement("span");
  labelRow.textContent = "Speak phrase(s) (optional)";
  labelRow.setAttribute("style", "font-weight: 500; font-size: 13px; color: #374151;");
  wrap.appendChild(labelRow);
  const body = document.createElement("div");
  body.setAttribute("style", "display: flex; flex-direction: column; gap: 6px;");
  wrap.appendChild(body);
  parent.appendChild(wrap);

  let mode: SpeakMode = "initial";
  let initialMode: SpeakMode = "initial";
  let cachedTextsets: SlotPickerOption[] = [];
  let sharedSelectedID: string = "";
  let nonSharedHandle: TextSetLVInputHandle | null = null;
  let nonSharedLVs: string[] = [];

  function makeSwitchButton(label: string, onClick: () => void): HTMLButtonElement {
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = label;
    b.setAttribute("style", "padding: 4px 10px; background: #e5e7eb; color: #111827; border: 0; border-radius: 4px; cursor: pointer; font-size: 12px; align-self: flex-start;");
    b.addEventListener("click", onClick);
    return b;
  }

  function makeBootstrapButton(label: string, recommended: boolean, onClick: () => void): HTMLButtonElement {
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = label;
    b.setAttribute("style", `padding: 6px 12px; background: ${recommended ? "#2563eb" : "#e5e7eb"}; color: ${recommended ? "white" : "#111827"}; border: 0; border-radius: 4px; cursor: pointer; font-size: 13px;`);
    b.addEventListener("click", onClick);
    return b;
  }

  function tearDownPickers(): void {
    if (nonSharedHandle) { nonSharedHandle.unmount(); nonSharedHandle = null; }
    body.replaceChildren();
  }

  function renderInitial(): void {
    tearDownPickers();
    const row = document.createElement("div");
    row.setAttribute("style", "display: flex; gap: 8px; flex-wrap: wrap;");
    row.appendChild(makeBootstrapButton("Use shared list", true, () => {
      sharedSelectedID = "";
      mode = "shared";
      renderShared();
    }));
    row.appendChild(makeBootstrapButton("Specify own list", false, () => {
      nonSharedLVs = [];
      mode = "non_shared";
      renderNonShared();
    }));
    body.appendChild(row);
  }

  function renderShared(): void {
    tearDownPickers();
    const sel = document.createElement("select");
    sel.setAttribute("style", "padding: 6px 10px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 14px; background: white;");
    const blank = document.createElement("option");
    blank.value = "";
    blank.textContent = "(unset)";
    sel.appendChild(blank);
    for (const o of cachedTextsets) {
      const opt = document.createElement("option");
      opt.value = o.id;
      opt.textContent = o.label;
      sel.appendChild(opt);
    }
    sel.value = sharedSelectedID;
    sel.addEventListener("change", () => { sharedSelectedID = sel.value; });
    body.appendChild(sel);
    body.appendChild(makeSwitchButton("Switch to own list", () => {
      nonSharedLVs = [];
      mode = "non_shared";
      renderNonShared();
    }));
  }

  function renderNonShared(): void {
    tearDownPickers();
    const host = document.createElement("div");
    body.appendChild(host);
    nonSharedHandle = mountTextSetLVInput(host, {
      initialLVs: nonSharedLVs,
      placeholder: "Spoken phrase variant (LV)",
      hideShortCodeFooter: true,
      onChange: (lvs) => { nonSharedLVs = lvs; },
    });
    body.appendChild(makeSwitchButton("Switch to shared list", () => {
      sharedSelectedID = "";
      mode = "shared";
      renderShared();
    }));
  }

  function rerender(): void {
    if (mode === "initial") renderInitial();
    else if (mode === "shared") renderShared();
    else renderNonShared();
  }

  rerender();

  return {
    root: wrap,
    value() {
      if (mode === "shared") {
        return { speak_textset_id: sharedSelectedID === "" ? null : sharedSelectedID };
      }
      if (mode === "non_shared") {
        const out: { speak_textset_members: string[]; speak_textset_short_code?: string } = {
          speak_textset_members: nonSharedHandle ? nonSharedHandle.value() : nonSharedLVs,
        };
        // Disconnect from a previously linked named TextSet: the server reads
        // `members + short_code: ""` as "create a fresh anonymous set" rather
        // than mutating the shared members in place (which would 422 with
        // shared_edit_requires_confirm).
        if (initialMode === "shared") {
          out.speak_textset_short_code = "";
        }
        return out;
      }
      return {};
    },
    setInitial(opts) {
      const lvMembers = opts.speakTextSetLVMembers ?? [];
      const sc = opts.speakTextSetShortCode ?? null;
      const id = opts.speakTextSetID ?? null;
      if (lvMembers.length > 0) {
        nonSharedLVs = lvMembers.slice();
        sharedSelectedID = "";
        mode = "non_shared";
      } else if (sc || id) {
        sharedSelectedID = id ?? "";
        nonSharedLVs = [];
        mode = "shared";
      } else {
        sharedSelectedID = "";
        nonSharedLVs = [];
        mode = "initial";
      }
      initialMode = mode;
      rerender();
    },
    refreshOptions(textsets) {
      cachedTextsets = textsets;
      if (mode === "shared") renderShared();
    },
    setVisible(v) {
      wrap.style.display = v ? "flex" : "none";
    },
  };
}

interface SlotWidgetOpts {
  // Text shown for the blank "(unset)" option. Ignored when allowUnset is false.
  unsetLabel?: string;
  // When false, the blank option is omitted entirely (slot must always have a type).
  allowUnset?: boolean;
  // Default Type when no value has been set yet (only meaningful when allowUnset=false).
  defaultType?: AdminActionType;
}

function mountSlotWidget(parent: HTMLElement, slotLabel: string, required: boolean, onChange: () => void, slotOpts?: SlotWidgetOpts): SlotWidget {
  const allowUnset = slotOpts?.allowUnset ?? true;
  const unsetLabel = slotOpts?.unsetLabel ?? "(unset)";
  const defaultType = slotOpts?.defaultType;

  const section = document.createElement("section");
  section.setAttribute("style", "display: flex; flex-direction: column; gap: 6px; padding: 8px; background: #fef9c3; border: 1px solid #fde047; border-radius: 4px;");

  const heading = document.createElement("h4");
  heading.textContent = slotLabel + (required ? "" : " (optional)");
  heading.setAttribute("style", "margin: 0; font-size: 13px; color: #713f12; font-weight: 600;");
  section.appendChild(heading);

  const typeSel = makeSelect();
  if (allowUnset) {
    const blankOpt = document.createElement("option");
    blankOpt.value = "";
    blankOpt.textContent = unsetLabel;
    typeSel.appendChild(blankOpt);
  }
  for (const t of Object.keys(ACTION_TYPE_LABELS) as AdminActionType[]) {
    const opt = document.createElement("option");
    opt.value = t;
    opt.textContent = ACTION_TYPE_LABELS[t];
    typeSel.appendChild(opt);
  }
  if (!allowUnset && defaultType) {
    typeSel.value = defaultType;
  }
  field(section, "Type", typeSel);

  const speakWidget = mountSpeakTextSetWidget(section);

  const nextTaskSel = makeSelect();
  const nextTaskField = document.createElement("div");
  nextTaskField.setAttribute("style", "display: flex; flex-direction: column; gap: 4px;");
  field(nextTaskField, "Next task (required)", nextTaskSel);
  const ntHint = document.createElement("p");
  ntHint.textContent = "Restricted to tasks within this card.";
  ntHint.setAttribute("style", "margin: 0; font-size: 11px; color: #6b7280;");
  nextTaskField.appendChild(ntHint);
  section.appendChild(nextTaskField);

  parent.appendChild(section);

  function fillSelect(sel: HTMLSelectElement, emptyLabel: string, opts: SlotPickerOption[], selectedID?: string | null): void {
    const prev = sel.value;
    sel.replaceChildren();
    const blank = document.createElement("option");
    blank.value = "";
    blank.textContent = emptyLabel;
    sel.appendChild(blank);
    for (const o of opts) {
      const opt = document.createElement("option");
      opt.value = o.id;
      opt.textContent = o.label;
      sel.appendChild(opt);
    }
    sel.value = selectedID ?? prev;
  }

  let cachedTasks: SlotPickerOption[] = [];

  function applyTypeVisibility(): void {
    const t = typeSel.value;
    speakWidget.setVisible(t !== "");
    nextTaskField.style.display = t === "next_task" ? "flex" : "none";
  }
  applyTypeVisibility();

  typeSel.addEventListener("change", () => {
    if (typeSel.value !== "next_task") nextTaskSel.value = "";
    applyTypeVisibility();
    onChange();
  });
  nextTaskSel.addEventListener("change", onChange);

  return {
    root: section,
    value(): AdminTaskActionDTO | null {
      const t = typeSel.value;
      if (t === "") return null;
      const out: AdminTaskActionDTO = { type: t as AdminActionType };
      const speakVal = speakWidget.value();
      if (speakVal.speak_textset_id !== undefined) out.speak_textset_id = speakVal.speak_textset_id;
      if (speakVal.speak_textset_short_code !== undefined) {
        out.speak_textset_short_code = speakVal.speak_textset_short_code;
      }
      if (speakVal.speak_textset_members !== undefined) {
        out.speak_textset_members = speakVal.speak_textset_members;
      }
      if (t === "next_task" && nextTaskSel.value !== "") out.next_task_id = nextTaskSel.value;
      return out;
    },
    setInitial(a: AdminTaskActionDTO | null): void {
      if (!a || !a.type) {
        typeSel.value = !allowUnset && defaultType ? defaultType : "";
      } else {
        typeSel.value = a.type;
      }
      speakWidget.setInitial({
        speakTextSetID: a?.speak_textset_id ?? null,
        speakTextSetShortCode: a?.speak_textset_short_code ?? null,
        speakTextSetLVMembers: a?.speak_textset_members ?? [],
      });
      fillSelect(nextTaskSel, "(unset)", cachedTasks, a?.next_task_id ?? null);
      applyTypeVisibility();
    },
    refreshOptions(textsets: SlotPickerOption[], tasks: SlotPickerOption[]): void {
      cachedTasks = tasks;
      speakWidget.refreshOptions(textsets);
      fillSelect(nextTaskSel, "(unset)", tasks, nextTaskSel.value || null);
    },
  };
}

function taskActionEqual(a: AdminTaskActionDTO | null | undefined, b: AdminTaskActionDTO | null | undefined): boolean {
  const an = a ?? null;
  const bn = b ?? null;
  if (an === null && bn === null) return true;
  if (an === null || bn === null) return false;
  if ((an.type ?? "") !== (bn.type ?? "")) return false;
  if ((an.next_task_id ?? "") !== (bn.next_task_id ?? "")) return false;
  const aLVs = an.speak_textset_members;
  const bLVs = bn.speak_textset_members;
  if ((aLVs && aLVs.length > 0) || (bLVs && bLVs.length > 0)) {
    return JSON.stringify(aLVs ?? []) === JSON.stringify(bLVs ?? []);
  }
  return (an.speak_textset_id ?? "") === (bn.speak_textset_id ?? "");
}

export function mount(root: HTMLElement, props?: unknown): () => void {
  const p = props as AdminRouteProps | undefined;
  const isNew = p?.kind === "admin-task-new";
  const gc = p && (p.kind === "admin-task-edit" || p.kind === "admin-task-new") ? p.gameShortCode : "";
  const cc = p && (p.kind === "admin-task-edit" || p.kind === "admin-task-new") ? p.cardShortCode : "";
  const ts = p && p.kind === "admin-task-edit" ? p.taskShortCode : "";

  const main = renderAdminShell(root, [
    { label: "Games", href: "/admin/games" },
    { label: `Game: ${gc || "?"}`, href: `/admin/games/${encodeURIComponent(gc)}` },
    { label: `Card: ${cc || "?"}`, href: `/admin/games/${encodeURIComponent(gc)}/cards/${encodeURIComponent(cc)}` },
    { label: isNew ? "Task: new" : `Task: ${ts || "?"}` },
  ]);

  const status = document.createElement("p");
  status.textContent = "Loading…";
  status.setAttribute("style", "color: #6b7280; font-size: 14px;");
  main.appendChild(status);

  const form = document.createElement("form");
  form.setAttribute("style", "display: none; flex-direction: column; gap: 12px; max-width: 720px; background: white; padding: 16px; border-radius: 6px; border: 1px solid #e5e7eb;");
  main.appendChild(form);

  const errorBox = document.createElement("div");
  errorBox.setAttribute("style", "margin-top: 12px; padding: 12px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; display: none;");
  main.appendChild(errorBox);

  const shortCodeInput = makeInput("text");
  if (isNew) {
    shortCodeInput.title = "Required; cannot be changed after creation.";
    shortCodeInput.placeholder = "short_code (immutable after creation)";
    shortCodeInput.required = true;
  } else {
    shortCodeInput.readOnly = true;
    shortCodeInput.title = "short_code is immutable";
    shortCodeInput.setAttribute("style", "padding: 6px 10px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 14px; background: #f3f4f6; color: #6b7280;");
  }
  const maxTriesInput = makeInput("number");

  const llmUserPromptW: OverridePromptHandle = mountOverridePromptField(
    "Override LLM user prompt (inherit from Card/Game/Settings when off)",
    promptVarsInfoBox(USER_PROMPT_VARS, "User-prompt substitution variables:"),
    6,
  );
  const descriptionTA: HTMLTextAreaElement = makePromptTextarea("", 4);

  const correctAnswerInput = makeInput("text");
  correctAnswerInput.setAttribute("lang", "lv");
  correctAnswerInput.spellcheck = false;
  const taskInstructionHost = document.createElement("div");
  const altAcceptedHost = document.createElement("div");

  // Migration 0045: explicit instruction source dropdown. The historical
  // implicit "task textset present? else game default" fallback is now
  // an explicit author choice — see internal/admin/tasks.go cascade-clear.
  const instructionSourceSelect = document.createElement("select");
  instructionSourceSelect.setAttribute("style",
    "padding: 6px 10px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 14px; max-width: 320px;");
  for (const [val, lab] of [
    ["game_default", "Use game's default instructions"],
    ["task", "Use task's instructions"],
    ["none", "Use no instructions"],
  ] as const) {
    const opt = document.createElement("option");
    opt.value = val;
    opt.textContent = lab;
    instructionSourceSelect.appendChild(opt);
  }
  instructionSourceSelect.value = "game_default";
  const instructionSourceHint = document.createElement("p");
  instructionSourceHint.setAttribute("style",
    "margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-style: italic;");

  function applyInstructionSourceVisibility(): void {
    const v = instructionSourceSelect.value;
    if (v === "task") {
      taskInstructionHost.style.display = "";
      instructionSourceHint.textContent = "";
      instructionSourceHint.style.display = "none";
    } else {
      taskInstructionHost.style.display = "none";
      instructionSourceHint.style.display = "block";
      instructionSourceHint.textContent = v === "none"
        ? "No instructions will play for this task."
        : "Falls back to the game's default task instructions.";
    }
  }
  instructionSourceSelect.addEventListener("change", applyInstructionSourceVisibility);

  field(form, "Short code", shortCodeInput);
  field(form, "Instruction source", instructionSourceSelect,
    "Where the spoken task instruction comes from. The TextSet picker below applies only when “Use task’s instructions” is selected.");
  form.appendChild(instructionSourceHint);
  field(form, "Task instruction(s)", taskInstructionHost,
    "Spoken to kid upon starting a task. If multiple are set, one will be selected at random.");
  field(form, "Correct answer", correctAnswerInput,
    "Sent to LLM. Can be sent to STT engine to better recognized the answer.");
  field(form, "Alternative accepted answer(s)", altAcceptedHost);
  field(form, "Description for LLM prompt", descriptionTA);

  // ── No-input section (migration 0043) ────────────────────────────
  // When checked, the kid loop plays the prompt audio, waits N ms,
  // and auto-advances via on_correct — no mic, no STT, no LLM. The
  // four non-on_correct routing slots are forced off in this state
  // (admin handler rejects with 422 no_input_other_slots_forbidden;
  // DB CHECK task_no_input_only_on_correct backs it up).
  const noInputSection = document.createElement("section");
  noInputSection.setAttribute("style", "display: flex; flex-direction: column; gap: 6px; padding: 10px; background: #ecfdf5; border: 1px solid #6ee7b7; border-radius: 4px;");
  const noInputRow = document.createElement("label");
  noInputRow.setAttribute("style", "display: flex; gap: 8px; align-items: center; font-size: 13px; color: #064e3b; font-weight: 500;");
  const noInputCheckbox = document.createElement("input");
  noInputCheckbox.type = "checkbox";
  noInputRow.appendChild(noInputCheckbox);
  const noInputLab = document.createElement("span");
  noInputLab.textContent = "No input from kid";
  noInputRow.appendChild(noInputLab);
  noInputSection.appendChild(noInputRow);
  const noInputTip = document.createElement("span");
  noInputTip.textContent = "Just speak the text in task instructions/show or update image and/or text and wait for a couple of seconds";
  noInputTip.setAttribute("style", "font-size: 11px; color: #047857; font-style: italic; margin-left: 22px;");
  noInputSection.appendChild(noInputTip);

  const noInputWaitRow = document.createElement("label");
  noInputWaitRow.setAttribute("style", "display: none; flex-direction: column; gap: 4px; font-size: 13px; color: #064e3b; margin-left: 22px;");
  const noInputWaitLab = document.createElement("span");
  noInputWaitLab.textContent = "Wait (ms)";
  noInputWaitLab.setAttribute("style", "font-weight: 500;");
  noInputWaitRow.appendChild(noInputWaitLab);
  const noInputWaitInput = makeInput("number");
  noInputWaitInput.min = "1";
  noInputWaitInput.step = "100";
  noInputWaitInput.placeholder = "3000 (default)";
  noInputWaitInput.setAttribute("style", "padding: 6px 10px; border: 1px solid #6ee7b7; border-radius: 4px; font-size: 14px; max-width: 180px;");
  noInputWaitRow.appendChild(noInputWaitInput);
  const noInputWaitTip = document.createElement("span");
  noInputWaitTip.textContent = "Empty = use the server default (3000 ms).";
  noInputWaitTip.setAttribute("style", "font-size: 11px; color: #047857; font-style: italic;");
  noInputWaitRow.appendChild(noInputWaitTip);
  noInputSection.appendChild(noInputWaitRow);

  form.appendChild(noInputSection);

  // Routing actions
  const actionsHost = document.createElement("section");
  actionsHost.setAttribute("style", "display: flex; flex-direction: column; gap: 10px; padding: 10px; background: #fef9c3; border: 1px solid #fde047; border-radius: 4px;");
  const actionsTitle = document.createElement("h3");
  actionsTitle.textContent = "Routing actions";
  actionsTitle.setAttribute("style", "margin: 0; font-size: 14px; color: #713f12;");
  actionsHost.appendChild(actionsTitle);

  // Migration 0043 — when "No input from kid" is on, the four non-on_correct
  // slots are unreachable; surface that to the operator and visually hide
  // those widgets so they can't be edited.
  const actionsNoInputNote = document.createElement("p");
  actionsNoInputNote.textContent = "With “No input from kid” enabled, only “On correct” fires after the wait.";
  actionsNoInputNote.setAttribute("style", "margin: 0; font-size: 12px; color: #713f12; font-style: italic; display: none;");
  actionsHost.appendChild(actionsNoInputNote);

  const slotWidgets = {
    on_correct: mountSlotWidget(actionsHost, "On correct", true, () => {}, {
      allowUnset: false,
      defaultType: "next_card",
    }),
    on_incorrect_tries_left: mountSlotWidget(actionsHost, "On incorrect (tries left)", false, () => {}, {
      unsetLabel: "(unset — same as on correct)",
    }),
    on_incorrect_no_tries_left: mountSlotWidget(actionsHost, "On incorrect (no tries left)", false, () => {}, {
      unsetLabel: "(unset — same as on incorrect tries left)",
    }),
    on_i_dont_know_tries_left: mountSlotWidget(actionsHost, "On “I don’t know” (tries left)", false, () => {}, {
      unsetLabel: "(unset — same as on incorrect no tries left)",
    }),
    on_i_dont_know_no_tries_left: mountSlotWidget(actionsHost, "On “I don’t know” (no tries left)", false, () => {}, {
      unsetLabel: "(unset — same as on “I don’t know” tries left)",
    }),
  };
  form.appendChild(actionsHost);

  // Slots that the no-input variant hides + force-clears on save. Kept
  // as a const so the apply function below stays terse.
  const noInputHiddenSlotKeys = [
    "on_incorrect_tries_left",
    "on_incorrect_no_tries_left",
    "on_i_dont_know_tries_left",
    "on_i_dont_know_no_tries_left",
  ] as const;

  function applyNoInputVisibility(): void {
    const on = noInputCheckbox.checked;
    noInputWaitRow.style.display = on ? "flex" : "none";
    actionsNoInputNote.style.display = on ? "block" : "none";
    for (const k of noInputHiddenSlotKeys) {
      slotWidgets[k].root.style.display = on ? "none" : "flex";
      if (on) {
        // Clear the widget's value so a stale slot doesn't survive in
        // the patch when the operator first ticks the box.
        slotWidgets[k].setInitial(null);
      }
    }
  }
  noInputCheckbox.addEventListener("change", applyNoInputVisibility);
  applyNoInputVisibility();

  // Image upload
  const imgSection = document.createElement("section");
  imgSection.setAttribute("style", "display: flex; flex-direction: column; gap: 8px; padding: 10px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px;");
  const imgTitle = document.createElement("h3");
  imgTitle.textContent = "Image";
  imgTitle.setAttribute("style", "margin: 0; font-size: 14px; color: #111827;");
  imgSection.appendChild(imgTitle);

  const pathLabel = document.createElement("p");
  pathLabel.textContent = "(no image)";
  pathLabel.setAttribute("style", "margin: 0; font-size: 12px; color: #6b7280; font-family: ui-monospace, monospace;");
  imgSection.appendChild(pathLabel);

  const imagePreview = document.createElement("img");
  imagePreview.setAttribute("alt", "task image preview");
  imagePreview.setAttribute("style", "max-width: 240px; max-height: 240px; border-radius: 4px; display: none;");
  imgSection.appendChild(imagePreview);

  const uploadRow = document.createElement("div");
  uploadRow.setAttribute("style", "display: flex; gap: 8px; align-items: center;");
  const uploadInput = document.createElement("input");
  uploadInput.type = "file";
  uploadInput.accept = "image/jpeg,image/png,image/webp";
  uploadInput.setAttribute("style", "font-size: 13px;");
  const uploadBtn = document.createElement("button");
  uploadBtn.type = "button";
  uploadBtn.textContent = "Upload image";
  uploadBtn.setAttribute("style", "padding: 6px 12px; background: #2563eb; color: white; border: 0; border-radius: 4px; cursor: pointer; font-size: 13px;");
  const removeImageBtn = document.createElement("button");
  removeImageBtn.type = "button";
  removeImageBtn.textContent = "Remove image";
  removeImageBtn.setAttribute("style", "padding: 6px 12px; background: #dc2626; color: white; border: 0; border-radius: 4px; cursor: pointer; font-size: 13px; display: none;");
  uploadRow.append(uploadInput, uploadBtn, removeImageBtn);
  if (isNew) {
    const note = document.createElement("p");
    note.textContent = "Save the task first to upload an image.";
    note.setAttribute("style", "margin: 0; font-size: 12px; color: #6b7280; font-style: italic;");
    imgSection.appendChild(note);
  } else {
    imgSection.appendChild(uploadRow);
  }
  form.appendChild(imgSection);

  // ── Text to be shown (with optional effect) ────────────────────────
  // Migration 0039: displayed_text is independent of correct_answer. The
  // effect picker uses displayed_text for the grapheme split.
  const teSection = document.createElement("section");
  teSection.setAttribute("style", "display: flex; flex-direction: column; gap: 8px; padding: 10px; background: #f0f9ff; border: 1px solid #93c5fd; border-radius: 4px;");
  const teTitle = document.createElement("h3");
  teTitle.textContent = "Text to be shown (with optional effect)";
  teTitle.setAttribute("style", "margin: 0; font-size: 14px; color: #0c4a6e;");
  teSection.appendChild(teTitle);

  const displayedTextInput = document.createElement("input");
  displayedTextInput.type = "text";
  displayedTextInput.lang = "lv";
  displayedTextInput.spellcheck = false;
  displayedTextInput.placeholder = "Leave empty for image-only";
  displayedTextInput.setAttribute("style", "padding: 6px 10px; border: 1px solid #93c5fd; border-radius: 4px; font-size: 14px; background: white;");
  const displayedTextWrap = document.createElement("label");
  displayedTextWrap.setAttribute("style", "display: flex; flex-direction: column; gap: 4px; font-size: 13px; color: #0c4a6e;");
  const displayedTextLab = document.createElement("span");
  displayedTextLab.textContent = "Text to display";
  displayedTextLab.setAttribute("style", "font-weight: 500;");
  displayedTextWrap.appendChild(displayedTextLab);
  displayedTextWrap.appendChild(displayedTextInput);
  teSection.appendChild(displayedTextWrap);

  const teTypeRow = document.createElement("div");
  teTypeRow.setAttribute("style", "display: flex; gap: 8px; align-items: center; font-size: 13px; color: #0c4a6e;");
  const teTypeLab = document.createElement("span");
  teTypeLab.textContent = "effect";
  teTypeLab.setAttribute("style", "font-weight: 500;");
  const teTypeSel = document.createElement("select");
  teTypeSel.setAttribute("style", "padding: 4px 8px; border: 1px solid #93c5fd; border-radius: 4px; font-size: 13px; background: white;");
  // Migration 0039: "plain" retired — "(none)" is plain display.
  for (const [v, label] of [["", "(none)"], ["highlighted_letter", "highlighted_letter"], ["missing_letter", "missing_letter"]] as const) {
    const opt = document.createElement("option");
    opt.value = v;
    opt.textContent = label;
    teTypeSel.appendChild(opt);
  }
  teTypeRow.append(teTypeLab, teTypeSel);
  teSection.appendChild(teTypeRow);

  const teHelp = document.createElement("p");
  teHelp.setAttribute("style", "margin: 0; font-size: 12px; color: #0c4a6e;");
  teSection.appendChild(teHelp);

  const tePickerHost = document.createElement("div");
  tePickerHost.setAttribute("style", "display: flex; flex-direction: column; gap: 4px;");
  teSection.appendChild(tePickerHost);

  form.appendChild(teSection);

  form.appendChild(llmUserPromptW.container);
  field(form, "Max tries (override default)", maxTriesInput);

  const buttonRow = document.createElement("div");
  buttonRow.setAttribute("style", "display: flex; gap: 8px; margin-top: 8px;");
  const saveBtn = document.createElement("button");
  saveBtn.type = "submit";
  saveBtn.textContent = isNew ? "Create" : "Save";
  saveBtn.setAttribute("style", "padding: 8px 14px; background: #2563eb; color: white; border: 0; border-radius: 6px; cursor: pointer; font-size: 14px;");
  const deleteBtn = document.createElement("button");
  deleteBtn.type = "button";
  deleteBtn.textContent = "Delete";
  deleteBtn.setAttribute("style", "padding: 8px 14px; background: #dc2626; color: white; border: 0; border-radius: 6px; cursor: pointer; font-size: 14px; margin-left: auto;");
  buttonRow.append(saveBtn);
  if (!isNew) buttonRow.append(deleteBtn);
  form.appendChild(buttonRow);

  let task: AdminTaskDTO | null = null;
  let card: AdminCardDTO | null = null;
  let currentTextEffect: AdminTextEffectDTO | null = null;
  let currentTextEffectDirty = false;
  let teUnmount: (() => void) | null = null;
  let taskInstructionWidget: SwitchableTextSetHandle | null = null;
  let altAcceptedWidget: StringListHandle | null = null;

  async function rerenderEffectPicker(): Promise<void> {
    if (teUnmount) { teUnmount(); teUnmount = null; }
    tePickerHost.replaceChildren();
    const effectType = currentTextEffect?.effect_type ?? "";
    if (effectType === "" || effectType === undefined) {
      teHelp.textContent = displayedTextInput.value.trim() === ""
        ? "No text shown — the card is image-only."
        : "Plain text — no letter is highlighted or hidden.";
      return;
    }
    const word = displayedTextInput.value.trim();
    if (word === "") {
      teHelp.textContent = "Type the text to display first — the picker uses its grapheme split.";
      return;
    }
    teHelp.textContent = effectType === "missing_letter"
      ? "Pick exactly ONE grapheme to hide (radio)."
      : "Pick one or more graphemes to highlight (checkbox).";
    teUnmount = mountTextEffectEditor(
      tePickerHost,
      word,
      { effect_type: effectType, letter_indexes: currentTextEffect?.letter_indexes ?? [], text_short_code: "" },
      (next) => {
        currentTextEffect = {
          effect_type: next.effect_type as AdminTextEffectType,
          letter_indexes: next.letter_indexes,
        };
        currentTextEffectDirty = true;
      },
    );
  }

  function fillForm(t: AdminTaskDTO): void {
    shortCodeInput.value = t.short_code ?? "";
    maxTriesInput.value = t.max_tries == null ? "" : String(t.max_tries);
    llmUserPromptW.setValue(t.llm_user_prompt);
    descriptionTA.value = t.description ?? "";
    correctAnswerInput.value = t.correct_answer ?? "";
    instructionSourceSelect.value = t.instruction_source ?? "game_default";
    applyInstructionSourceVisibility();
    taskInstructionWidget?.setInitial({
      shortCode: t.task_instruction_textset_short_code ?? null,
      members: t.task_instruction_members ?? [],
    });
    altAcceptedWidget?.setValue(t.alt_accepted_answers ?? []);
    // Migration 0043: hydrate no-input checkbox + wait override BEFORE
    // setting slot initials, so applyNoInputVisibility can decide
    // whether to clear the hidden slots.
    noInputCheckbox.checked = !!t.no_input_from_kid;
    noInputWaitInput.value = t.no_input_wait_ms == null ? "" : String(t.no_input_wait_ms);
    slotWidgets.on_correct.setInitial(t.on_correct ?? null);
    slotWidgets.on_incorrect_tries_left.setInitial(t.on_incorrect_tries_left ?? null);
    slotWidgets.on_incorrect_no_tries_left.setInitial(t.on_incorrect_no_tries_left ?? null);
    slotWidgets.on_i_dont_know_tries_left.setInitial(t.on_i_dont_know_tries_left ?? null);
    slotWidgets.on_i_dont_know_no_tries_left.setInitial(t.on_i_dont_know_no_tries_left ?? null);
    applyNoInputVisibility();
    if (t.image_path) {
      pathLabel.textContent = t.image_path;
      imagePreview.setAttribute("src", `/storage/images/${t.image_path}`);
      imagePreview.style.display = "block";
      removeImageBtn.style.display = "";
    } else {
      pathLabel.textContent = "(no image)";
      imagePreview.removeAttribute("src");
      imagePreview.style.display = "none";
      removeImageBtn.style.display = "none";
    }
    displayedTextInput.value = t.displayed_text ?? "";
    currentTextEffect = t.text_effect ? { ...t.text_effect } : null;
    currentTextEffectDirty = false;
    teTypeSel.value = currentTextEffect?.effect_type ?? "";
    void rerenderEffectPicker();
  }

  function onTypeChange(): void {
    const next = teTypeSel.value as AdminTextEffectType | "";
    if (next === "") {
      currentTextEffect = null;
    } else {
      currentTextEffect = {
        effect_type: next,
        letter_indexes: [],
      };
    }
    currentTextEffectDirty = true;
    void rerenderEffectPicker();
  }

  async function load(): Promise<void> {
    try {
      const g = await adminGet<AdminGameDTO>(
        `/api/admin/games/by-short-code/${encodeURIComponent(gc)}`,
      );
      if (!g.id) throw new Error("game has no id");
      const textsetRows = await adminGet<AdminTextSetDTO[]>("/api/admin/textsets");
      const textsetOpts: SlotPickerOption[] = textsetRows
        .filter((x) => x.id != null)
        .map((x) => ({
          id: x.id!,
          label: `${x.short_code ?? "?"}: ${x.name ?? "(unnamed)"}`,
        }));
      const instructionTextsetOpts: SwitchableTextSetOption[] = textsetRows
        .filter((x) => x.short_code)
        .map((x) => ({
          short_code: x.short_code!,
          label: `${x.short_code}: ${x.name ?? "(unnamed)"}`,
        }));
      const cards = await adminGet<AdminCardDTO[]>(
        `/api/admin/games/${encodeURIComponent(g.id)}/cards`,
      );
      const foundCard = cards.find((x) => x.short_code === cc);
      if (!foundCard || !foundCard.id) {
        status.textContent = `Card “${cc}” not found.`;
        return;
      }
      card = foundCard;

      const cardTasks = await adminGet<AdminTaskDTO[]>(
        `/api/admin/cards/${encodeURIComponent(foundCard.id)}/tasks`,
      );
      const taskOpts: SlotPickerOption[] = cardTasks
        .filter((t) => t.id != null)
        .map((t) => ({ id: t.id!, label: t.short_code ?? "?" }));
      for (const sw of Object.values(slotWidgets)) {
        sw.refreshOptions(textsetOpts, taskOpts);
      }

      if (isNew) {
        taskInstructionWidget = mountSwitchableTextSetWidget(taskInstructionHost, {
          placeholder: "Task instruction variant (LV)",
        });
        taskInstructionWidget.refreshOptions(instructionTextsetOpts);
        taskInstructionWidget.setInitial({ shortCode: null, members: [] });
        applyInstructionSourceVisibility();
        altAcceptedWidget = mountStringListInput(altAcceptedHost, {
          initial: [],
          placeholder: "Alternative accepted answer",
        });
        displayedTextInput.addEventListener("input", () => { void rerenderEffectPicker(); });
        status.style.display = "none";
        form.style.display = "flex";
        return;
      }
      const foundTask = cardTasks.find((x) => x.short_code === ts);
      if (!foundTask) {
        status.textContent = `Task “${ts}” not found in card “${cc}”.`;
        return;
      }
      task = foundTask;
      taskInstructionWidget = mountSwitchableTextSetWidget(taskInstructionHost, {
        placeholder: "Task instruction variant (LV)",
      });
      taskInstructionWidget.refreshOptions(instructionTextsetOpts);
      taskInstructionWidget.setInitial({
        shortCode: foundTask.task_instruction_textset_short_code ?? null,
        members: foundTask.task_instruction_members ?? [],
      });
      altAcceptedWidget = mountStringListInput(altAcceptedHost, {
        initial: foundTask.alt_accepted_answers ?? [],
        placeholder: "Alternative accepted answer",
      });
      // Migration 0039: the effect picker reads displayed_text (not
      // correct_answer) for the grapheme split.
      displayedTextInput.addEventListener("input", () => { void rerenderEffectPicker(); });
      fillForm(foundTask);
      status.style.display = "none";
      form.style.display = "flex";
    } catch (err) {
      if (err instanceof AdminError && (err.status === 401 || err.status === 403)) {
        status.textContent = "Not signed in — visit /login to authenticate, then refresh.";
      } else {
        status.textContent = `Failed to load: ${(err as Error).message}`;
      }
    }
  }

  async function onUpload(): Promise<void> {
    if (!task || !task.id || !card || !card.id) return;
    const file = uploadInput.files && uploadInput.files[0];
    if (!file) {
      showAdminToast(document.body, "Pick a file first");
      return;
    }
    try {
      const result = await adminUploadImage(card.id, file);
      const tasksURL = `/api/admin/tasks/${encodeURIComponent(task.id)}`;
      const updated = await adminPatch<Partial<AdminTaskDTO>, AdminTaskDTO>(
        tasksURL,
        { image_path: result.image_path },
      );
      task = updated;
      imagePreview.setAttribute("src", result.image_url);
      imagePreview.style.display = "block";
      pathLabel.textContent = result.image_path;
      removeImageBtn.style.display = "";
      showAdminToast(document.body, "Image uploaded");
      errorBox.style.display = "none";
    } catch (err) {
      const msg = err instanceof AdminError
        ? `Upload failed (${err.status}): ${err.bodyText.slice(0, 200)}`
        : `Upload failed: ${(err as Error).message}`;
      errorBox.textContent = msg;
      errorBox.style.display = "block";
    }
  }

  async function onCreate(e: Event): Promise<void> {
    e.preventDefault();
    if (!card || !card.id) return;
    const sc = shortCodeInput.value.trim();
    if (!sc) {
      errorBox.textContent = "short_code is required";
      errorBox.style.display = "block";
      shortCodeInput.focus();
      return;
    }
    // Migration 0043: validate no-input fields before assembling body.
    const noInputOn = noInputCheckbox.checked;
    const ca = correctAnswerInput.value.trim();
    if (!ca && !noInputOn) {
      errorBox.textContent = "correct_answer is required";
      errorBox.style.display = "block";
      correctAnswerInput.focus();
      return;
    }
    let noInputWaitNew: number | null = null;
    if (noInputWaitInput.value !== "") {
      const n = Number(noInputWaitInput.value);
      if (!Number.isFinite(n) || n <= 0) {
        errorBox.textContent = "Wait (ms) must be a positive number";
        errorBox.style.display = "block";
        noInputWaitInput.focus();
        return;
      }
      noInputWaitNew = n;
    }
    const instructionSource = instructionSourceSelect.value as
      "game_default" | "task" | "none";
    const body: Partial<AdminTaskDTO> = {
      short_code: sc,
      correct_answer: ca,
      instruction_source: instructionSource,
    };
    // Migration 0045: only send the TextSet payload when source === "task".
    // The server cascade-clears the FK for the other two values regardless,
    // but omitting keeps the wire shape clean.
    if (instructionSource === "task") {
      const instructionVal = taskInstructionWidget?.value();
      if (instructionVal) {
        if (instructionVal.mode === "shared") {
          const sc = instructionVal.shortCode ?? "";
          if (sc !== "") body.task_instruction_textset_short_code = sc;
        } else if ((instructionVal.members ?? []).length > 0) {
          body.task_instruction_members = instructionVal.members;
        }
      }
    }
    const altNew = altAcceptedWidget?.value() ?? [];
    if (altNew.length > 0) body.alt_accepted_answers = altNew;
    if (maxTriesInput.value !== "") body.max_tries = Number(maxTriesInput.value);
    const userPromptOverride = llmUserPromptW.value();
    if (userPromptOverride) body.llm_user_prompt = userPromptOverride;
    const descriptionNew = descriptionTA.value;
    if (descriptionNew) body.description = descriptionNew;
    const displayedTextNew = displayedTextInput.value;
    if (displayedTextNew) body.displayed_text = displayedTextNew;
    body.no_input_from_kid = noInputOn;
    if (noInputOn && noInputWaitNew != null) body.no_input_wait_ms = noInputWaitNew;
    const slotKeys: (keyof typeof slotWidgets)[] = [
      "on_correct",
      "on_incorrect_tries_left",
      "on_incorrect_no_tries_left",
      "on_i_dont_know_tries_left",
      "on_i_dont_know_no_tries_left",
    ];
    for (const k of slotKeys) {
      // Migration 0043: when no-input is on, the four non-on_correct
      // slot widgets are hidden — never include them in the create body.
      if (noInputOn && k !== "on_correct") continue;
      const next = slotWidgets[k].value();
      if (next) (body as Partial<Record<string, AdminTaskActionDTO | null>>)[k] = next;
    }
    // Migration 0039: text_effect requires non-empty displayed_text — drop
    // the effect if the user left displayed_text blank, so we don't ship an
    // inconsistent shape the server CHECK constraint will reject.
    if (currentTextEffect && displayedTextNew) {
      body.text_effect = {
        effect_type: currentTextEffect.effect_type,
        letter_indexes: currentTextEffect.letter_indexes ?? [],
      };
    }
    try {
      const created = await adminPost<Partial<AdminTaskDTO>, AdminTaskDTO>(
        `/api/admin/cards/${encodeURIComponent(card.id)}/tasks`,
        body,
      );
      errorBox.style.display = "none";
      showAdminToast(document.body, `Created ${created.short_code ?? sc}`);
      const target = `/admin/games/${encodeURIComponent(gc)}/cards/${encodeURIComponent(cc)}/tasks/${encodeURIComponent(created.short_code ?? sc)}`;
      navigateAdmin(target);
    } catch (err) {
      let msg: string;
      if (err instanceof AdminError && err.status === 409) {
        msg = "short_code already exists in this card";
      } else if (err instanceof AdminError && err.status === 422) {
        msg = `Invalid input: ${err.bodyText.slice(0, 200)}`;
      } else if (err instanceof AdminError) {
        msg = `Create failed (${err.status}): ${err.bodyText.slice(0, 200)}`;
      } else {
        msg = `Create failed: ${(err as Error).message}`;
      }
      errorBox.textContent = msg;
      errorBox.style.display = "block";
    }
  }

  async function onSave(e: Event): Promise<void> {
    e.preventDefault();
    if (!task || !task.id) return;
    const patch: Partial<AdminTaskDTO> = {};
    if (shortCodeInput.value !== (task.short_code ?? "")) {
      patch.short_code = shortCodeInput.value;
    }
    const caNew = correctAnswerInput.value;
    if (caNew !== (task.correct_answer ?? "")) {
      patch.correct_answer = caNew;
    }
    // Migration 0045: instruction_source enum. Always include in the patch
    // when it changed; only ship the TextSet payload when the post-patch
    // source is "task" (server cascade-clears the FK for the other values).
    const instructionSourceNew = instructionSourceSelect.value as
      "game_default" | "task" | "none";
    const instructionSourceOld = task.instruction_source ?? "game_default";
    if (instructionSourceNew !== instructionSourceOld) {
      patch.instruction_source = instructionSourceNew;
    }
    if (instructionSourceNew === "task") {
      const instructionVal = taskInstructionWidget?.value();
      if (instructionVal) {
        const origSC = task.task_instruction_textset_short_code ?? null;
        const origMembers = task.task_instruction_members ?? [];
        if (instructionVal.mode === "shared") {
          const newSC = instructionVal.shortCode ?? "";
          if (newSC !== (origSC ?? "")) {
            patch.task_instruction_textset_short_code = newSC;
          }
        } else {
          const newMembers = instructionVal.members ?? [];
          const wasShared = !!origSC;
          const membersChanged = JSON.stringify(newMembers) !== JSON.stringify(origMembers);
          if (wasShared || membersChanged) {
            patch.task_instruction_members = newMembers;
            // Disconnect from the shared named TextSet (server reads members
            // + "" short_code as "create a fresh anonymous set" rather than
            // editing the shared members in place).
            if (wasShared) {
              patch.task_instruction_textset_short_code = "";
            }
          }
        }
      }
    }
    const altNew = altAcceptedWidget?.value() ?? [];
    if (JSON.stringify(altNew) !== JSON.stringify(task.alt_accepted_answers ?? [])) {
      patch.alt_accepted_answers = altNew;
    }
    const maxTriesNew = maxTriesInput.value === "" ? null : Number(maxTriesInput.value);
    if ((task.max_tries ?? null) !== maxTriesNew) {
      patch.max_tries = maxTriesNew;
    }
    const userPromptOverride = llmUserPromptW.value();
    if ((task.llm_user_prompt ?? "") !== userPromptOverride) {
      patch.llm_user_prompt = userPromptOverride;
    }
    const descriptionNew = descriptionTA.value;
    if ((task.description ?? "") !== descriptionNew) {
      patch.description = descriptionNew;
    }
    // Migration 0039: displayed_text PATCH semantics — "" clears, "abc" sets.
    const displayedTextNew = displayedTextInput.value;
    if ((task.displayed_text ?? "") !== displayedTextNew) {
      patch.displayed_text = displayedTextNew;
    }
    // Migration 0043: no-input flag + wait (ms). Validate the wait first;
    // PATCH always sends the flag (so a flip → server) and clears the
    // four non-on_correct slots in the same patch when on.
    const noInputOn = noInputCheckbox.checked;
    const noInputWaitNew = noInputWaitInput.value === "" ? null : Number(noInputWaitInput.value);
    if (noInputOn && noInputWaitNew != null && (!Number.isFinite(noInputWaitNew) || noInputWaitNew <= 0)) {
      errorBox.textContent = "Wait (ms) must be a positive number";
      errorBox.style.display = "block";
      noInputWaitInput.focus();
      return;
    }
    const noInputWasOn = !!task.no_input_from_kid;
    if (noInputOn !== noInputWasOn) {
      patch.no_input_from_kid = noInputOn;
    }
    const noInputPrevWait = task.no_input_wait_ms ?? null;
    if (noInputOn) {
      if (noInputWaitNew !== noInputPrevWait) patch.no_input_wait_ms = noInputWaitNew;
    } else if (noInputPrevWait != null) {
      // The server clears the wait whenever the flag is off, but be
      // explicit in the patch so an export-roundtrip doesn't drift.
      patch.no_input_wait_ms = null;
    }

    const slotKeys: (keyof typeof slotWidgets)[] = [
      "on_correct",
      "on_incorrect_tries_left",
      "on_incorrect_no_tries_left",
      "on_i_dont_know_tries_left",
      "on_i_dont_know_no_tries_left",
    ];
    for (const k of slotKeys) {
      // When no-input is on, force the four non-on_correct slot patches
      // to null so the server clears any prior values (the handler
      // refuses the patch otherwise).
      if (noInputOn && k !== "on_correct") {
        const prev = (task as Partial<Record<string, AdminTaskActionDTO | null>>)[k];
        if (prev != null) {
          (patch as Partial<Record<string, AdminTaskActionDTO | null>>)[k] = null;
        }
        continue;
      }
      const next = slotWidgets[k].value();
      const prev = (task as Partial<Record<string, AdminTaskActionDTO | null>>)[k];
      if (!taskActionEqual(next, prev)) {
        (patch as Partial<Record<string, AdminTaskActionDTO | null>>)[k] = next;
      }
    }
    // Migration 0039: text_effect requires non-empty displayed_text. If the
    // user cleared displayed_text while the existing task had an effect,
    // cascade-clear the effect in the same patch — otherwise the server
    // ends up with effect set + null displayed_text and the CHECK fires.
    if (displayedTextNew === "" && (task.text_effect || currentTextEffect)) {
      patch.text_effect = null;
    } else if (currentTextEffectDirty) {
      if (currentTextEffect) {
        patch.text_effect = {
          effect_type: currentTextEffect.effect_type,
          letter_indexes: currentTextEffect.letter_indexes ?? [],
        };
      } else {
        patch.text_effect = null;
      }
    }
    const cardPath = `/admin/games/${encodeURIComponent(gc)}/cards/${encodeURIComponent(cc)}`;
    if (Object.keys(patch).length === 0) {
      navigateAdmin(cardPath);
      showAdminToast(document.body, "No changes to save");
      return;
    }
    try {
      const updated = await saveWithSharedEditConfirm<Partial<AdminTaskDTO>, AdminTaskDTO>(
        `/api/admin/tasks/${encodeURIComponent(task.id)}`,
        patch,
      );
      task = updated;
      errorBox.style.display = "none";
      navigateAdmin(cardPath);
      showAdminToast(document.body, "Saved");
    } catch (err) {
      const msg = err instanceof AdminError
        ? `Save failed (${err.status}): ${err.bodyText.slice(0, 200)}`
        : `Save failed: ${(err as Error).message}`;
      errorBox.textContent = msg;
      errorBox.style.display = "block";
    }
  }

  async function onRemoveImage(): Promise<void> {
    if (!task || !task.id || !task.image_path) return;
    if (!window.confirm(`Remove image “${task.image_path}” from this task?`)) return;
    try {
      const updated = await adminPatch<Partial<AdminTaskDTO>, AdminTaskDTO>(
        `/api/admin/tasks/${encodeURIComponent(task.id)}`,
        { image_path: "" },
      );
      task = updated;
      uploadInput.value = "";
      pathLabel.textContent = "(no image)";
      imagePreview.removeAttribute("src");
      imagePreview.style.display = "none";
      removeImageBtn.style.display = "none";
      showAdminToast(document.body, "Image removed");
      errorBox.style.display = "none";
    } catch (err) {
      const msg = err instanceof AdminError
        ? `Remove failed (${err.status}): ${err.bodyText.slice(0, 200)}`
        : `Remove failed: ${(err as Error).message}`;
      errorBox.textContent = msg;
      errorBox.style.display = "block";
    }
  }

  async function onDelete(): Promise<void> {
    if (!task || !task.id) return;
    if (!window.confirm(`Delete task ${task.short_code}? This cannot be undone.`)) return;
    try {
      await adminDelete(`/api/admin/tasks/${encodeURIComponent(task.id)}`);
      showAdminToast(document.body, "Deleted");
      navigateAdmin(`/admin/games/${encodeURIComponent(gc)}/cards/${encodeURIComponent(cc)}`);
    } catch (err) {
      const msg = err instanceof AdminError
        ? `Delete failed (${err.status}): ${err.bodyText.slice(0, 200)}`
        : `Delete failed: ${(err as Error).message}`;
      errorBox.textContent = msg;
      errorBox.style.display = "block";
    }
  }

  const submitHandler = isNew ? onCreate : onSave;
  if (!isNew) uploadBtn.addEventListener("click", onUpload);
  if (!isNew) removeImageBtn.addEventListener("click", onRemoveImage);
  form.addEventListener("submit", submitHandler);
  if (!isNew) deleteBtn.addEventListener("click", onDelete);
  teTypeSel.addEventListener("change", onTypeChange);

  void load();

  return () => {
    if (!isNew) uploadBtn.removeEventListener("click", onUpload);
    if (!isNew) removeImageBtn.removeEventListener("click", onRemoveImage);
    form.removeEventListener("submit", submitHandler);
    if (!isNew) deleteBtn.removeEventListener("click", onDelete);
    teTypeSel.removeEventListener("change", onTypeChange);
    if (teUnmount) { teUnmount(); teUnmount = null; }
    taskInstructionWidget?.unmount();
    altAcceptedWidget?.unmount();
  };
}
